import { Box } from "@mantine/core";
import { AppRoute } from "app";
import { useAtomValue, useSetAtom } from "jotai";
import { colors } from "theme";

import BottomDrawer from "components/BottomDrawer";
import Button from "components/Button";
import ClientTranslation from "components/ClientTranslation";
import Flex from "components/Flex";
import Image from "components/Image";
import StickyButtonsBox from "components/StickyButtonsBox";
import Text from "components/Text";
import SpeedyPaymentTips from "components/Tips";
import { TipVariant } from "components/Tips/types";

import { clientConfigAtom } from "atoms/clientConfig";
import { estimateTotalAtom } from "atoms/estimate";
import { pageDrawerAtom } from "atoms/pageDrawer";
import { useClientTranslation } from "hooks/useClientTranslation";
import { usePageRouting } from "hooks/usePageRouting";
import { usePageTracking } from "hooks/usePageTracking";

import { useSubmitOfferResponse } from "./hooks/useSubmitOfferResponse";
import { useStyles } from "./styles";
import { usePullEstimate } from "./hooks/pullEstimate";

export const SpeedyPayment = () => {
  const { speedyPayment } = useAtomValue(clientConfigAtom);

  const { t } = useClientTranslation();
  usePageTracking({ name: "Speedy payment - view" });

  const { classes } = useStyles();
  const { acceptSTPOffer, declineSTPOffer, acceptSTPOfferPayee } = useSubmitOfferResponse();
  const toggleDeclineModal = useSetAtom(pageDrawerAtom);

  const amount = useAtomValue(estimateTotalAtom);
  const { estimate: estimateConfig, enablePayeeJourney = false } = useAtomValue(clientConfigAtom);
  const { navigateTo } = usePageRouting();


  usePullEstimate(enablePayeeJourney)
  
  const tips: TipVariant[] = enablePayeeJourney ? ["iban", "money"] : ["money", "email", "phoneCall"];
  const acceptHandler = () => !enablePayeeJourney ? acceptSTPOffer() : acceptSTPOfferPayee();
  const handleDecline = () => {
    if (estimateConfig?.repairable?.withNegotiation) {
      navigateTo(AppRoute.NegotiateOffer);
    } else {
      declineSTPOffer();
    }
  };

  return (
    <Flex width="100%" align="center">
      <Image
        src="/assets/svg/speedy-payment-accepted.svg"
        alt="Accept Cash"
        overrides={speedyPayment?.intro?.illustrations?.main}
      />
      <Flex align="center" className={classes.description}>
        <Text dataTestId="speedyPayment" as="h1" sizeVariant="xl" weight={500}>
          {t("speedy_payment_options.header")}
        </Text>
        <Text sizeVariant="xxxl" weight={400}>
          {amount}
        </Text>

        <Text sizeVariant="md" weight={400} mt={24}>
          <ClientTranslation i18nKey="speedy_payment_options.description" amount={amount}>
            We can offer {{ amount }} today to cover your vehicle repair. The payment will be sent to your bank account
            within <b>2 days.</b>\n\nDeclining this offer may result in a longer claim resolution time."
          </ClientTranslation>
        </Text>
      </Flex>
      <Box px={16} mt={24} mb={32}>
        <SpeedyPaymentTips
          title={t("speedy_payment_options.instruction.header")}
          description={t("speedy_payment_options.instruction.body")}
          tips={tips}
          size="large"
          borderRadius="20px"
          gap={16}
          py={24}
          px={12}
        />
      </Box>

      <StickyButtonsBox shadow={true}>
        <Button 
          {...(speedyPayment?.showDeclineAsPrimaryButtonVariant ? {} : {typeVariant: "secondary"}) }
          mb={16}
          onClick={toggleDeclineModal}
        >
          {t("global.decline")}
        </Button>
        <Button onClick={acceptHandler}>{t("global.accept")}</Button>
      </StickyButtonsBox>

      <BottomDrawer closeOnClickOutside={true} heading={t("decline_offer.header")} withStickyButton={false}>
        <Flex direction="column" pb={32}>
          <Text sizeVariant="md">{t("decline_offer.instruction")}</Text>
          <Button typeVariant="secondary" mb={16} mt={24} onClick={toggleDeclineModal}>
            {t("global.cancel")}
          </Button>
          <Button onClick={handleDecline} style={{ backgroundColor: colors.red[50] }}>
            {t("global.decline")}
          </Button>
        </Flex>
      </BottomDrawer>
    </Flex>
  );
};
